import {
    ChatMessage, SendFileMessage,
    SendMediumMessage,
    SendMessage,
    SendTextMessage, SendVoiceMessage
} from "@web-libs/features/chat/domain/types/chat-message.types";
import {SetChatFunction, UpsertChatMessage} from "@web-libs/features/chat/application/messenger/use-chat-messenger";
import {ChatRepository} from "@web-libs/features/chat/domain/chat.repository";
import {buildErrorHandler} from "@web-libs/features/chat/application/messenger/sending-error-handler";
import {setPendingTimeout} from "@web-libs/features/chat/application/messenger/pending-timeout";

export const useMessageResending = (setChatFunction:SetChatFunction, upsertChatMessage: UpsertChatMessage,chatRepository: ChatRepository) => {

    const _withSendingErrorHandler = buildErrorHandler(upsertChatMessage);

    const resendMessage = async (chatroomId: string, message: ChatMessage) => {
        if(message.internalReferenceId === undefined){
            throw new Error("Trying to resend message without internalReferenceId")
        }
        const internalReferenceId = message.internalReferenceId;
        const optimisticMessage: ChatMessage = {
            ...message,
            deliveryStatus: "pending",
        }
        upsertChatMessage(chatroomId, optimisticMessage);
        const sendMessage: SendMessage = {
            type: message.type,
            internalReferenceId: internalReferenceId
        }
        switch (message.type) {
            case "text":
                await _resendTextMessage(chatroomId, message, sendMessage);
                break;
            case "medium":
                await _resendMediaMessage(chatroomId, message, sendMessage);
                break;
            case "file":
                await _resendFileMessage(chatroomId, message, sendMessage);
                break;
            case "voice":
                await _resendVoiceMessage(chatroomId, message, sendMessage);
                break;
        }
        setPendingTimeout(setChatFunction, chatroomId, internalReferenceId)
    }

    const _resendTextMessage = async (chatroomId: string, message: ChatMessage, sendMessage:SendMessage) => {
        if(!message.text || message.type !== "text"){
            throw new Error("Message text is missing in TextMessage or type incompatible")
        }
        const sendTextMessage: SendTextMessage = {
            ...sendMessage,
            type: "text",
            text: message.text
        }
        await _withSendingErrorHandler(() => chatRepository.sendMessage(chatroomId, sendTextMessage), message, chatroomId);
    }

    const _resendMediaMessage = async (chatroomId: string, message: ChatMessage, sendMessage:SendMessage) => {
        if(!message.contentUrl || message.type !== "medium" || !message.fileBackupForResend){
            throw new Error("Message contentUrl or fileBackupForResend is missing in MediumMessage or type incompatible")
        }
        const sendMediumMessage: SendMediumMessage = {
            ...sendMessage,
            type: "medium",
            medium: message.fileBackupForResend,
        }
        await _withSendingErrorHandler(() => chatRepository.sendMedium(chatroomId, sendMediumMessage), message, chatroomId);
    }

    const _resendFileMessage = async (chatroomId: string, message: ChatMessage, sendMessage:SendMessage) => {
        if(!message.contentUrl || message.type !== "file" || !message.fileBackupForResend){
            throw new Error("Message contentUrl or fileBackupForResend is missing in FileMessage or type incompatible")
        }
        const sendFileMessage: SendFileMessage = {
            ...sendMessage,
            type: "file",
            file: message.fileBackupForResend,
        }
        await _withSendingErrorHandler(() => chatRepository.sendFile(chatroomId, sendFileMessage), message, chatroomId);
    }

    const _resendVoiceMessage = async (chatroomId: string, message: ChatMessage, sendMessage:SendMessage) => {
        if(!message.contentUrl || message.type !== "voice" || !message.blobBackupForResend){
            throw new Error("Message contentUrl or blobBackupForResend is missing in VoiceMessage or type incompatible")
        }
        const sendVoiceMessage: SendVoiceMessage = {
            ...sendMessage,
            type: "voice",
            blob: message.blobBackupForResend,
            duration: message.duration!
        }
        await _withSendingErrorHandler(() => chatRepository.sendVoice(chatroomId, sendVoiceMessage), message, chatroomId);
    }

    return {
        resendMessage
    }
}
